/**
 * Event screen — narrative risk/reward node.
 *
 * Renders the event text + its choices. Gated choices (gold/program) are
 * disabled with a reason. A choice whose outcome removes a card opens a
 * card-removal picker before resolving. All resolution goes through
 * store.resolveEvent(choiceId, removeCardUid?).
 */

import { el, div, button } from "../dom";
import { topbar, cardRow } from "../widgets";
import { getEventDef } from "../../engine";
import type { Store } from "../store";
import type { EventChoice } from "../../engine";

/** Pending removal choice (set when a choice requires picking a card). */
let pendingRemoveChoiceId: string | null = null;

export function resetEventUi(): void {
  pendingRemoveChoiceId = null;
}

export function renderEvent(store: Store): HTMLElement {
  const run = store.run!;
  const def = getEventDef(run.pendingEvent!);

  const screen = div("screen");
  screen.appendChild(topbar(run, () => store.open("deck")));

  const body = div("screen__body scroll");

  body.appendChild(el("div", { class: "h-title", text: def.name }));
  body.appendChild(
    el("div", {
      class: "muted",
      style: "white-space:normal;line-height:1.5;font-size:13px;max-width:640px",
      text: def.text,
    }),
  );

  if (pendingRemoveChoiceId) {
    // Card removal picker.
    body.appendChild(
      el("div", { class: "h-sub", text: "REMOVE WHICH CARD?", style: "margin-top:8px" }),
    );
    const list = div("col scroll");
    list.style.maxHeight = "300px";
    for (const card of run.deck) {
      list.appendChild(
        cardRow(card.defId, {
          onClick: () => {
            const choiceId = pendingRemoveChoiceId!;
            pendingRemoveChoiceId = null;
            store.resolveEvent(choiceId, card.uid);
          },
        }),
      );
    }
    body.appendChild(list);
    body.appendChild(
      button("btn btn--ghost", "← cancel", () => {
        pendingRemoveChoiceId = null;
        store.requestRender();
      }),
    );
  } else {
    const choices = div("col");
    choices.style.marginTop = "8px";
    for (const choice of def.choices) {
      const { disabled, reason } = gateChoice(choice, run);
      const label = choice.label + (reason ? `  (${reason})` : "");
      choices.appendChild(
        button("btn btn--wide choice", label, () => onChoice(store, choice), {
          disabled,
        }),
      );
    }
    body.appendChild(choices);
  }

  screen.appendChild(body);
  return screen;
}

function gateChoice(
  choice: EventChoice,
  run: Store["run"],
): { disabled: boolean; reason: string } {
  const r = run!;
  if (choice.requiresGold != null && r.gold < choice.requiresGold) {
    return { disabled: true, reason: `need ${choice.requiresGold}₵` };
  }
  if (choice.requiresProgram && r.programs.length === 0) {
    return { disabled: true, reason: "need a Program" };
  }
  // A removeCard choice needs at least one card to remove.
  const needsRemovable = choice.outcomes.some((o) => o.kind === "removeCard");
  if (needsRemovable && r.deck.length === 0) {
    return { disabled: true, reason: "no cards" };
  }
  return { disabled: false, reason: "" };
}

function onChoice(store: Store, choice: EventChoice): void {
  const removes = choice.outcomes.some((o) => o.kind === "removeCard");
  if (removes) {
    pendingRemoveChoiceId = choice.id;
    store.requestRender();
    return;
  }
  store.resolveEvent(choice.id);
}
